<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  currentInstallmentId: { type: [Number, String], default: null },
  installments: { type: Array, default: () => [] },
});

const emit = defineEmits(['download-sheet', 'open-installment']);
const { t } = useI18n();
const multiple = computed(() => props.installments.length > 1);
const statusClass = color =>
  ({
    neutral: 'bg-slate-100 text-slate-700',
    yellow: 'bg-yellow-100 text-yellow-800',
    primary: 'bg-blue-100 text-blue-800',
    green: 'bg-emerald-100 text-emerald-800',
    red: 'bg-red-100 text-red-800',
  })[color] || 'bg-slate-100 text-slate-700';
const isCurrent = installment =>
  String(installment.id) === String(props.currentInstallmentId);
</script>

<template>
  <section
    data-testid="finance-installment-schedule"
    class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
  >
    <h3 class="font-semibold text-lais-text">
      {{ t('REAL_ESTATE_FINANCE.DETAIL.INSTALLMENT_SCHEDULE') }}
    </h3>
    <div class="mt-4 grid gap-3 lg:grid-cols-2">
      <article
        v-for="installment in installments"
        :key="installment.id"
        class="rounded-lais border p-4"
        :class="
          isCurrent(installment)
            ? 'border-lais-primary bg-lais-primary/5'
            : 'border-lais-border-soft bg-white'
        "
      >
        <div class="flex flex-wrap items-start justify-between gap-2">
          <p class="font-semibold text-lais-text">
            {{
              multiple
                ? t('REAL_ESTATE_FINANCE.DETAIL.INSTALLMENT_NUMBER', {
                    number: installment.numero,
                  })
                : t('REAL_ESTATE_FINANCE.DETAIL.INTEGRAL')
            }}
          </p>
          <span
            class="rounded-full px-2 py-1 text-xs font-bold"
            :class="statusClass(installment.status?.color)"
          >
            {{ installment.status?.label }}
          </span>
        </div>
        <p class="mt-3 font-redHatDisplay text-2xl font-bold text-lais-text">
          {{
            installment.valor?.amount ||
            t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY')
          }}
        </p>
        <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt class="text-lais-text-muted">
              {{ t('REAL_ESTATE_FINANCE.DETAIL.PAYMENT_PERIOD') }}
            </dt>
            <dd class="mt-1 font-medium text-lais-text">
              {{
                installment.periodo_pagamento ||
                t('REAL_ESTATE_FINANCE.TABLE.NOT_INFORMED')
              }}
            </dd>
          </div>
          <div>
            <dt class="text-lais-text-muted">
              {{ t('REAL_ESTATE_FINANCE.DETAIL.FORECAST') }}
            </dt>
            <dd class="mt-1 font-medium text-lais-text">
              {{
                installment.previsao ||
                t('REAL_ESTATE_FINANCE.TABLE.NOT_INFORMED')
              }}
            </dd>
          </div>
        </dl>
        <div class="mt-4 flex flex-wrap items-center gap-2">
          <LaisPrimaryButton
            v-if="
              isCurrent(installment) &&
              installment.planilha?.disponivel &&
              installment.planilha?.pode_baixar
            "
            size="sm"
            variant="ghost"
            @click="emit('download-sheet', installment.planilha)"
          >
            {{ t('REAL_ESTATE_FINANCE.ALLOCATION.VIEW_SHEET') }}
          </LaisPrimaryButton>
          <span
            v-else-if="
              isCurrent(installment) && !installment.planilha?.disponivel
            "
            class="rounded-lais bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700"
          >
            {{ t('REAL_ESTATE_FINANCE.ALLOCATION.SHEET_PENDING') }}
          </span>
          <LaisPrimaryButton
            v-if="!isCurrent(installment)"
            size="sm"
            variant="ghost"
            @click="emit('open-installment', installment.id)"
          >
            {{ t('REAL_ESTATE_FINANCE.DETAIL.VIEW_INSTALLMENT_STATUS') }}
          </LaisPrimaryButton>
        </div>
      </article>
    </div>
  </section>
</template>
